import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sparkles, Send } from 'lucide-react';
import { getStoredToken } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FinanceAssistant = ({
  endpoint = '/api/finance/assistant',
  title = 'AI Finance Assistant',
  description = "Ask questions about Suubi's finances — answers use live platform data",
  suggestions,
} = {}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: 'user', content: question }, { id: crypto.randomUUID(), role: 'assistant', content: '' }]);
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getStoredToken()}`,
        },
        body: JSON.stringify({ question, session_id: sessionId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.session_id) setSessionId(ev.session_id);
          if (ev.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.delta };
              return copy;
            });
          }
          if (ev.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], role: 'assistant', content: `Sorry — ${ev.error}` };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: 'assistant', content: 'Sorry, I could not answer that right now. Please try again.' };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const SUGGESTIONS = suggestions || [
    'How much did the school spend this month?',
    'What are our biggest expenses?',
    'How much is outstanding in women loans?',
    'Compare school expenses with last month',
  ];

  return (
    <Card data-testid="finance-assistant">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-amber-500" /> {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <Button key={s} size="sm" variant="outline" className="text-xs h-7" onClick={() => ask(s)} data-testid="assistant-suggestion">
                {s}
              </Button>
            ))}
          </div>
        )}
        {messages.length > 0 && (
          <div ref={scrollRef} className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="assistant-messages">
            {messages.map((m, i) => (
              <div key={m.id} className={`text-sm rounded-lg px-3 py-2 whitespace-pre-wrap ${m.role === 'user' ? 'bg-primary/10 ml-8' : 'bg-muted mr-8'}`}>
                {m.content || (busy && i === messages.length - 1 ? 'Thinking…' : '')}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ask()}
            placeholder="e.g. How much income did the women project generate?"
            disabled={busy}
            data-testid="assistant-input"
          />
          <Button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="assistant-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
